import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './MeetSignupSettings.module.css';
import { ChevronDown, ChevronRight } from 'lucide-react';
import CustomDatePicker from 'components/common/CustomDatePicker';
import CustomTimePicker from 'components/common/CustomTimePicker';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

// §6.8 — Collapsible card (mirrors MaxEventsPerGroup / HighJumpSettings) for the meet-signup config of a multi-club
// meet. Two toggles at different trust levels (D10): private self-service ("My Registrations") and the public
// landing page. Public requires self-service, so enabling it auto-enables self-service, and turning self-service
// off turns public off. The signup window + cap apply to both surfaces. The short code is server-minted on save.
const MeetSignupSettings = ({
  selfServiceEnabled = false,
  publicEnabled = false,
  signupCode = null,
  openDate = '',
  openTime = '',
  closeDate = '',
  closeTime = '',
  registrationCap = 0,
  errors = {},
  onChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [copied, setCopied] = useState(false);

  const publicUrl = signupCode ? `${window.location.origin}/m/${signupCode}` : null;

  // Public depends on self-service (the public funnel IS self-service for externals).
  const handleSelfServiceToggle = (checked) => {
    onChange('selfServiceRegistrationEnabled', checked);
    if (!checked && publicEnabled) onChange('publicSignupEnabled', false);
  };

  const handlePublicToggle = (checked) => {
    onChange('publicSignupEnabled', checked);
    if (checked && !selfServiceEnabled) onChange('selfServiceRegistrationEnabled', true);
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (e.g. insecure context); the URL is visible to copy manually.
    }
  };

  const subtitle = publicEnabled
    ? 'Public page published'
    : selfServiceEnabled
      ? 'Self-service on'
      : 'Self-service off';

  return (
    <div className={styles.container}>
      <div
        className={`${styles.header}${isCollapsed ? ` ${styles.headerCollapsed}` : ''}`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className={styles.headerContent}>
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
          <h3 className={styles.title}>Meet Signup</h3>
          <span className={styles.subtitle}>{subtitle}</span>
        </div>
      </div>

      {!isCollapsed && (
        <div className={styles.content}>
          {/* Toggle 1 — private self-service */}
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={selfServiceEnabled}
              onChange={(e) => handleSelfServiceToggle(e.target.checked)}
              className={styles.checkbox}
            />
            <span className={styles.toggleText}>
              <strong>Allow self-service registration</strong>
              <span className={styles.toggleHint}>
                Members, guests &amp; visitors of this club can manage their own registrations for this meet.
              </span>
            </span>
          </label>

          {/* Toggle 2 — public landing page (requires self-service); shareable link sits right-aligned on this row */}
          <div className={styles.publishRow}>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={publicEnabled}
                onChange={(e) => handlePublicToggle(e.target.checked)}
                className={styles.checkbox}
              />
              <span className={styles.toggleText}>
                <strong>Publish public registration page</strong>
                <span className={styles.toggleHint}>
                  Anyone with the link can register. Turning this on also allows self-service above.
                </span>
              </span>
            </label>
            {publicEnabled &&
              (publicUrl ? (
                <div className={styles.urlInline}>
                  <a
                    className={styles.urlLink}
                    href={publicUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {publicUrl}
                  </a>
                  <CustomNewButton variant="outline" onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </CustomNewButton>
                </div>
              ) : (
                <span className={styles.urlPending}>Link generated when you save</span>
              ))}
          </div>

          {/* Window + cap — apply to both surfaces; shown once self-service is on */}
          {selfServiceEnabled && (
            <div className={styles.settingsArea}>
              <div className={styles.windowGrid}>
                <div className={styles.windowCol}>
                  <span className={styles.windowLabel}>Registration opens</span>
                  <div className={styles.dateTimeRow}>
                    <CustomDatePicker
                      label="Date"
                      name="signupOpenDate"
                      value={openDate}
                      error={errors.signupOpenDate}
                      onChange={(e) => onChange('signupOpenDate', e.target.value)}
                    />
                    <CustomTimePicker
                      label="Time"
                      placeholder="00:00"
                      value={openTime}
                      style={{ marginBottom: '20px' }}
                      onTimeChange={(t) => onChange('signupOpenTime', t)}
                    />
                  </div>
                </div>
                <div className={styles.windowCol}>
                  <span className={styles.windowLabel}>Registration closes</span>
                  <div className={styles.dateTimeRow}>
                    <CustomDatePicker
                      label="Date"
                      name="signupCloseDate"
                      value={closeDate}
                      error={errors.signupCloseDate}
                      onChange={(e) => onChange('signupCloseDate', e.target.value)}
                    />
                    <CustomTimePicker
                      label="Time"
                      placeholder="23:59"
                      value={closeTime}
                      style={{ marginBottom: '20px' }}
                      onTimeChange={(t) => onChange('signupCloseTime', t)}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.capRow}>
                <label className={styles.capLabel} htmlFor="registrationCap">
                  Total registration cap
                </label>
                <input
                  id="registrationCap"
                  type="number"
                  min="0"
                  className={styles.capInput}
                  value={registrationCap ?? 0}
                  onChange={(e) => onChange('registrationCap', e.target.value)}
                />
                <span className={styles.capHint}>0 = no limit (counts all registered athletes)</span>
              </div>
            </div>
          )}

          <div className={styles.helpText}>
            <p>
              <strong>💡 Tip:</strong> Times are in the club&apos;s timezone. Outside the open/close window only an
              admin can change registrations.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

MeetSignupSettings.propTypes = {
  selfServiceEnabled: PropTypes.bool,
  publicEnabled: PropTypes.bool,
  signupCode: PropTypes.string,
  openDate: PropTypes.string,
  openTime: PropTypes.string,
  closeDate: PropTypes.string,
  closeTime: PropTypes.string,
  registrationCap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

export default MeetSignupSettings;
